/**
 * ApprovalForm component
 *
 * Props:
 * - onClose: Callback function when form is closed
 * - assetId: ID of the asset
 */

import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import Loader from '~components/generic/loader/Loader';
import useLabels from '~hooks/useLabels';
import { postApprovalForm } from '~modules/media-library/controller/api/assets';
import { AssetId } from '~modules/media-library/model/Asset';
import { FieldValue } from '~modules/media-library/model/RaiseRequestFormSettings';
import { StatusActions } from '~store/actions';

type ApprovalFormProps = {
  onClose?: () => void;
  assetId: string;
};

export function ApprovalForm({ onClose, assetId }: ApprovalFormProps) {
  // State for form data values
  const [formData, setFormData] = useState<Record<string, FieldValue>>();

  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Track if form submission is complete
  const [isComplete, setIsComplete] = useState<boolean>(false);

  // error message for field 'message'
  const [messageError, setMessageError] = useState<string>(null);

  // info about current state
  const [statusMessage, setStatusMessage] = useState<string>(null);

  // Redux dispatch
  const dispatch = useDispatch();

  // Get translated labels
  const {
    hlContributeApporovalFormHeadline,
    msgContributeApporovalFormInfo,
    btnContributeApporovalFormOK,
    btnContributeApporovalFormReject,
    btnContributeApporovalFormApprove,
  } = useLabels([
    'hlContributeApporovalFormHeadline',
    'msgContributeApporovalFormInfo',
    'btnContributeApporovalFormOK',
    'btnContributeApporovalFormReject',
    'btnContributeApporovalFormApprove',
  ]);

  const useApprovalForm = (assetId: AssetId) => {
    return useMutation({
      mutationFn: async (postData: object) => await postApprovalForm(postData, assetId),
      onError: (err: any) => {
        if (err.status === 500 && err.error) {
          dispatch(StatusActions.setErrorNotification(err.error));
        }
        setIsLoading(false);
      },
      onSuccess: (data: any) => {
        if (data !== undefined) {
          if (data.status === true && isComplete === false) {
            setIsComplete(true);
            setStatusMessage(data.message);
          }
          if (data.errors && data.errors.children && data.errors.children.message) {
            setMessageError(data.errors.children.message.errors.join());
          }
        }
        setIsLoading(false);
      },
    });
  };

  const { mutate } = useApprovalForm(assetId);

  const onReject = useCallback(() => {
    setIsLoading(true);
    mutate({ ...formData, reject: '' });
  }, [formData, mutate]);

  const onApprove = useCallback(() => {
    setIsLoading(true);
    mutate({ ...formData, approve: '' });
  }, [formData, mutate]);

  const onValueChanged = (key: string, value: FieldValue) => {
    setFormData(currentData => {
      return { ...currentData, [key]: value };
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ minHeight: '200px' }} className="flex justify-center">
        <Loader />
      </div>
    );
  }

  // Show success message
  if (isComplete) {
    return (
      <div>
        <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
          <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
            {statusMessage}
          </h2>
        </header>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            {btnContributeApporovalFormOK}
          </button>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <>
      <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
        <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
          {hlContributeApporovalFormHeadline}
        </h2>
      </header>
      <div className=" container py-1 px-3">{msgContributeApporovalFormInfo}</div>
      <div className="raise-request-assets-form container py-1 px-3">
        <form>
          <textarea
            name="message"
            className="form-control w-full"
            onChange={e => onValueChanged('message', e.target.value)}
          ></textarea>
          {messageError && <div className="error c-hint--error text-sm">{messageError}</div>}
        </form>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onReject}>
            {btnContributeApporovalFormReject}
          </button>
          <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onApprove}>
            {btnContributeApporovalFormApprove}
          </button>
        </div>
      </div>
    </>
  );
}
